import { computed, type Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { PaymentService } from '@/services/payment.services';

export interface PaymentsStatsTotals {
  count: number;
  successCount: number;
  pendingCount: number;
  failedCount: number;
  successRate: number;
  grossAmount: number;
  netAmount: number;
  taxAmount: number;
  refundedAmount: number;
  averagePayment: number;
  currency: string;
}

export interface PaymentsStatsByProcessor extends PaymentsStatsTotals {
  processor: string;
}

export interface PaymentsStatsSeriesPoint {
  bucket: string;
  count: number;
  successCount: number;
  failedCount: number;
  grossAmount: number;
  refundedAmount: number;
}

export interface PaymentsStatsData {
  range: { from: string; to: string; granularity: string; timezone: string };
  totals: PaymentsStatsTotals;
  byProcessor: PaymentsStatsByProcessor[];
  series: PaymentsStatsSeriesPoint[];
}

const PROCESSOR_COLORS: Record<string, string> = {
  mock: '#94a3b8',
  market_pay: '#3b82f6',
  on_site: '#10b981',
  payfast: '#8b5cf6',
};

function formatAmount(n: number, cur?: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: cur ?? 'EUR',
    }).format(n ?? 0);
  } catch {
    return `${n ?? 0} ${cur ?? ''}`;
  }
}

export function usePaymentsStats(
  storeIds: Ref<string[]>,
  from: Ref<string>,
  to: Ref<string>,
  granularity: Ref<'day' | 'week' | 'month'>,
) {
  const { data, isPending, isError } = useQuery({
    queryKey: computed(() => [
      'payments-stats',
      storeIds.value.join(','),
      from.value,
      to.value,
      granularity.value,
    ]),
    queryFn: async () => {
      const res = await PaymentService.getPaymentStats({
        from: from.value || undefined,
        to: to.value || undefined,
        granularity: granularity.value,
        storeId: storeIds.value.length ? storeIds.value : undefined,
      });
      const d = res.data as any;
      return (d?.data ?? d) as PaymentsStatsData;
    },
    staleTime: 5 * 60_000,
  });

  const kpis = computed(() => {
    const t = data.value?.totals;
    if (!t) return [];
    return [
      {
        icon: 'pi-wallet',
        label: 'Total paiements',
        value: String(t.count ?? 0),
        desc: `Succès ${t.successCount ?? 0} · En attente ${t.pendingCount ?? 0} · Échoués ${t.failedCount ?? 0}`,
        accent: true,
      },
      {
        icon: 'pi-check-circle',
        label: 'Taux de succès',
        value: `${Math.round((t.successRate ?? 0) * 100)}%`,
        desc: `Sur ${t.count ?? 0} paiements`,
      },
      {
        icon: 'pi-money-bill',
        label: 'Montant brut',
        value: formatAmount(t.grossAmount, t.currency),
        desc: `Net ${formatAmount(t.netAmount, t.currency)}`,
      },
      {
        icon: 'pi-percentage',
        label: 'Taxes',
        value: formatAmount(t.taxAmount, t.currency),
        desc: `Panier moyen ${formatAmount(t.averagePayment, t.currency)}`,
      },
      {
        icon: 'pi-replay',
        label: 'Remboursé',
        value: formatAmount(t.refundedAmount, t.currency),
        desc: '',
      },
    ];
  });

  const chartData = computed(() => {
    const series = data.value?.series ?? [];
    return {
      values: series.map((s) => s.grossAmount ?? 0),
      labels: series.map((s) =>
        new Date(s.bucket).toLocaleDateString(undefined, {
          day: 'numeric',
          month: 'short',
        }),
      ),
    };
  });

  const donutSlices = computed(() => {
    const list = data.value?.byProcessor ?? [];
    const total = list.reduce((a, b) => a + (b.count ?? 0), 0) || 1;
    return list
      .map((p) => ({
        label: p.processor,
        value: Math.round(((p.count ?? 0) / total) * 100),
        color: PROCESSOR_COLORS[p.processor] ?? '#64748b',
      }))
      .filter((s) => s.value > 0);
  });

  return {
    data,
    kpis,
    chartData,
    donutSlices,
    isPending,
    isError,
  };
}
